/**

 * Policy detail — IMS Medical Agent API (medical-agent-api).

 * POL → Policy/* ; PROD → GetPolicyByIdAgent + Product/* + GetAgentRequestCardsAgent ; else → GetAgentRequestCards

 */

import {

  PolicyById,

  PolicyProgram,

  PolicyGroup,

  PolicyException,

  PolicyCard,

  ProductById,

  GetProductPrograms,

  GetProductGroups,

  GetProductExcepts,

  GetProducts,

  GetAgentRequestCardsAgent,

  GetPolicyByIdAgent,

} from "../Source/medicalAgent/index.js";

import { getPolicyDetailMock } from "./policyDetailMock.js";

import {

  loadAgentRequestDetail,

  buildDetailListRow,

  IS_POLICY_POL,

  IS_POLICY_PROD,

  normalizeIsPolicyKind,

  mapAgentRequestCardToTableRow,

  pickPublicLink,

} from "./agentRequestsApi.js";

import Utils from "../Source/utils.js";
import { i18n } from "../Source/lang/i18n.js";

const SILENT = { silent: true };

const POLICY_DETAIL_DEBUG =
  process.env.NODE_ENV === "development" ||
  process.env.VUE_APP_POLICY_DETAIL_DEBUG === "true";

const USE_MOCK =

  process.env.VUE_APP_USE_POLICY_MOCK === "true" ||

  process.env.VUE_APP_USE_POLICY_MOCK === "1";



/** Swagger Policy/* requests use policyId, not id. */
function policyRequest(policyId) {
  const n = Number(policyId);
  return {
    requestCorrelationId: crypto.randomUUID(),
    policyId: Number.isFinite(n) ? n : policyId,
  };
}



function productByIdRequest(id) {

  const n = Number(id);

  return {

    requestCorrelationId: crypto.randomUUID(),

    id: Number.isFinite(n) ? n : id,

  };

}



function productDetailsRequest(productId) {

  const n = Number(productId);

  return {

    requestCorrelationId: crypto.randomUUID(),

    productId: Number.isFinite(n) ? n : productId,

  };

}



function settledFailureMessage(settled) {
  if (!settled) return "";
  if (settled.status === "rejected") {
    return (
      Utils.FormatErrorWithCorrelationId(settled.reason) ||
      settled.reason?.message ||
      ""
    );
  }
  if (settled.status === "fulfilled" && settled.value?.data?.isSuccess === false) {
    return Utils.FormatErrorWithCorrelationId(null, settled.value) || "";
  }
  return "";
}

function notifyPolicyDetailPartial(...settleds) {
  for (const settled of settleds) {
    const msg = settledFailureMessage(settled);
    if (msg) {
      Utils.Alert(msg);
      return;
    }
  }
  Utils.Alert(i18n.global.t("policyDetailPartialHint"));
}

function pickData(response) {
  const body = response?.data;
  if (!body) return [];
  const raw = body.data ?? body.Data;
  if (raw == null) return [];
  return Array.isArray(raw) ? raw : [raw];
}

function pickProductExcepts(response) {
  const rows = pickData(response);
  if (rows.length) return rows;
  const body = response?.data;
  const nested = body?.data?.excepts ?? body?.data?.exceptions ?? body?.excepts;
  if (nested == null) return [];
  return Array.isArray(nested) ? nested : [nested];
}

function mapExceptNamesFallback(product) {
  if (!product || typeof product !== "object") return [];
  const names = product.exceptNames ?? product.ExceptNames ?? [];
  const list = Array.isArray(names) ? names : [];
  return list
    .map((name) => (name != null ? String(name).trim() : ""))
    .filter(Boolean)
    .map((exceptName) => ({
      groupName: "",
      exceptName,
      caseCount: "",
      countLimitTypeName: "",
      caseSum: "",
      sumLimitTypeName: "",
      useTypeName: "",
      franchise: "",
      notes: "",
    }));
}



function formatDateOnly(value) {

  if (value == null || value === "") return "";

  const s = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return s;

  return d.toISOString().slice(0, 10);

}

/** Fields used by PolicyDetailPage «Sığorta olunanlar» table only. */
export function mapPolicyCardToTableRow(row) {
  return mapAgentRequestCardToTableRow(row);
}

function mapPolicyCards(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapAgentRequestCardToTableRow).filter(Boolean);
}

function pickPolicyDateStr(source, strKey, dateKey) {
  const s = source?.[strKey];
  if (s != null && String(s).trim() !== "") return String(s).trim();
  return formatDateOnly(source?.[dateKey]) || "";
}

function pickDetailField(row, ...keys) {
  if (!row || typeof row !== "object") return null;
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
}

function pickInvoiceFields(source) {
  const invoiceNumber = pickDetailField(source, "invoiceNumber", "InvoiceNumber");
  const invoiceLink = pickDetailField(
    source,
    "invoiceLink",
    "InvoiceLink",
    "invoiceUrl",
    "InvoiceUrl"
  );
  return {
    invoiceNumber: invoiceNumber != null ? String(invoiceNumber).trim() : "",
    invoiceLink: invoiceLink != null ? String(invoiceLink).trim() : "",
  };
}

function mergeInvoiceFields(...sources) {
  let invoiceNumber = "";
  let invoiceLink = "";
  for (const src of sources) {
    const inv = pickInvoiceFields(src);
    if (!invoiceNumber && inv.invoiceNumber) invoiceNumber = inv.invoiceNumber;
    if (!invoiceLink && inv.invoiceLink) invoiceLink = inv.invoiceLink;
  }
  return { invoiceNumber, invoiceLink };
}

function withInvoice(medicalPolicy, ...sources) {
  return {
    ...medicalPolicy,
    invoice: mergeInvoiceFields(medicalPolicy?.invoice, ...sources),
  };
}

/** PolicyDetailPage contract block — only fields shown in UI. */
function mapPolicyByIdRow(row) {
  if (!row) return null;
  const mp =
    row.medicalPolicy && typeof row.medicalPolicy === "object" ? row.medicalPolicy : row;
  const insured =
    mp.insured && typeof mp.insured === "object" ? mp.insured : {};
  const beneficiary =
    mp.beneficiary && typeof mp.beneficiary === "object" ? mp.beneficiary : {};

  return {
    medicalPolicy: {
      policyId: mp.policyId ?? row.policyId,
      policyDateStr: pickPolicyDateStr(mp, "policyDateStr", "policyDate"),
      branchName: mp.branchName ?? row.branchName ?? "",
      policyFullNumber:
        mp.policyFullNumber ?? mp.fullPolicyNumber ?? row.fullPolicyNumber ?? "",
      startDateStr: pickPolicyDateStr(mp, "startDateStr", "startDate"),
      endDateStr: pickPolicyDateStr(mp, "endDateStr", "endDate"),
      cancelDateStr: pickPolicyDateStr(mp, "cancelDateStr", "cancelDate"),
      cancelTypeName: mp.cancelTypeName ?? row.cancelTypeName ?? "",
      cancelReason: mp.cancelReason ?? row.cancelReason ?? "",
      currency: mp.currency ?? row.currency ?? "",
      insured: {
        name: insured.name ?? mp.insuredName ?? row.insuredName ?? "",
        address: insured.address ?? mp.insuredAddress ?? row.insuredAddress ?? "",
        phone: insured.phone ?? mp.insuredPhone ?? row.insuredPhone ?? "",
      },
      beneficiary: {
        name: beneficiary.name ?? mp.benefName ?? row.benefName ?? "",
        address: beneficiary.address ?? mp.benefAddress ?? row.benefAddress ?? "",
      },
      invoice: mergeInvoiceFields(row, mp),
    },
    adminCharge: row.adminCharge ?? mp.adminCharge ?? null,
    limitPremium: row.limitPremium ?? mp.limitPremium ?? null,
    sellerLogin:
      row.sellerLogin ?? row.sellerName ?? mp.sellerLogin ?? mp.sellerName ?? null,
  };
}



function mapProductGroups(rows) {

  return rows.map((g) => ({

    ...g,

    sumScopeName:

      g.sumScopeName ?? (g.sumScope != null && g.sumScope !== "" ? String(g.sumScope) : ""),

  }));

}



function mapProductPrograms(rows) {

  return rows.map((p) => ({

    ...p,

    limitScopeName:

      p.limitScopeName ??

      (p.limitScope != null && p.limitScope !== "" ? String(p.limitScope) : ""),

  }));

}



function mapProductExceptions(rows) {

  return rows.map((e) => ({

    ...e,

    groupName: e.groupName ?? (e.groupId != null ? String(e.groupId) : ""),

    countLimitTypeName:

      e.countLimitTypeName ??

      (e.countLimitType != null ? String(e.countLimitType) : ""),

    sumLimitTypeName:

      e.sumLimitTypeName ?? (e.sumLimitType != null ? String(e.sumLimitType) : ""),

    useTypeName: e.useTypeName ?? (e.useType != null ? String(e.useType) : ""),

  }));

}



function buildMedicalPolicyFallback(policyId, listRow = null) {
  const policyNumber =
    listRow?.policyNumber && listRow.policyNumber !== "—"
      ? listRow.policyNumber
      : listRow?.productName && listRow.productName !== "—"
        ? listRow.productName
        : `POL-${policyId}`;
  const insuredName =
    listRow?.insuredClient && listRow.insuredClient !== "—" ? listRow.insuredClient : "";

  return {
    detailMode: "policy",
    policyId: String(policyId),
    policyDetailPartial: true,
    medicalPolicy: {
      policyId: Number(policyId) || policyId,
      policyDateStr: "",
      branchName: "",
      policyFullNumber: policyNumber,
      startDateStr: "",
      endDateStr: listRow?.endDate || "",
      cancelDateStr: "",
      cancelTypeName: "",
      cancelReason: "",
      currency: "",
      insured: { name: insuredName, address: "", phone: "" },
      beneficiary: { name: "", address: "" },
      invoice: mergeInvoiceFields(listRow),
    },
    adminCharge: null,
    limitPremium: null,
    sellerLogin: null,
    insuredCards: [],
    exceptions: [],
    programs: [],
    groups: [],
    statusDisplay: listRow?.statusName || listRow?.statusDisplay || "",
  };
}

async function loadMedicalPolicyDetail(policyId, listRow = null) {
  const req = policyRequest(policyId);
  if (POLICY_DETAIL_DEBUG) {
    console.info("[PolicyDetail] type=POL → POST Policy/*", {
      policyId: req.policyId,
      endpoints: [
        "Policy/PolicyById",
        "Policy/PolicyException",
        "Policy/Program",
        "Policy/Group",
        "Policy/PolicyCard",
      ],
    });
  }
  const [policyRes, excRes, progRes, groupRes, cardRes] = await Promise.all([
    PolicyById(req),
    PolicyException(req),
    PolicyProgram(req),
    PolicyGroup(req),
    PolicyCard(req),
  ]);
  if (POLICY_DETAIL_DEBUG) {
    console.info("[PolicyDetail] Policy/* responses", {
      policyById: policyRes?.data?.isSuccess,
      policyException: excRes?.data?.isSuccess,
      program: progRes?.data?.isSuccess,
      group: groupRes?.data?.isSuccess,
      policyCard: cardRes?.data?.isSuccess,
    });
  }

  const policyRow = pickData(policyRes)[0];
  const base = mapPolicyByIdRow(policyRow);

  if (!base) {
    notifyPolicyDetailPartial({ status: "fulfilled", value: policyRes });
    return {
      ...buildMedicalPolicyFallback(policyId, listRow),
      exceptions: pickData(excRes),
      programs: pickData(progRes),
      groups: pickData(groupRes),
      insuredCards: mapPolicyCards(pickData(cardRes)),
    };
  }

  return {
    detailMode: "policy",
    policyId: String(base.medicalPolicy?.policyId ?? policyId),
    ...base,
    medicalPolicy: withInvoice(base.medicalPolicy, listRow),
    exceptions: pickData(excRes),
    programs: pickData(progRes),
    groups: pickData(groupRes),
    insuredCards: mapPolicyCards(pickData(cardRes)),
  };
}



async function findProductInCatalog(productId) {
  try {
    const response = await GetProducts(
      { requestCorrelationId: crypto.randomUUID() },
      SILENT
    );
    const rows = pickData(response);
    const n = Number(productId);
    return (
      rows.find(
        (p) =>
          Number(p.productId) === n ||
          String(p.productId) === String(productId)
      ) ?? null
    );
  } catch {
    return null;
  }
}

function settledValue(settled) {
  if (!settled || settled.status !== "fulfilled") return null;
  return settled.value;
}

function productMedicalPolicyFallback(product, productId, listRow, displayName) {
  return {
    policyId: product.productId ?? productId,
    policyFullNumber: displayName || `PROD-${productId}`,
    policyDateStr: "",
    branchName: "",
    startDateStr: formatDateOnly(product.startDate),
    endDateStr: formatDateOnly(product.endDate ?? listRow?.endDate),
    cancelDateStr: "",
    cancelTypeName: "",
    cancelReason: "",
    currency: product.currency != null ? String(product.currency) : "",
    insured: {},
    beneficiary: {},
    invoice: mergeInvoiceFields(listRow),
  };
}

async function loadProductDetail(productId, listRow = null) {
  const tableReqId =
    listRow?.reqId != null && String(listRow.reqId).trim() !== ""
      ? String(listRow.reqId).trim()
      : "";
  const reqIdNum = Number(tableReqId);
  const hasReqId = Number.isFinite(reqIdNum) && reqIdNum > 0;

  const detailsReq = productDetailsRequest(productId);
  const [agentSettled, productSettled, progSettled, groupSettled, exceptSettled, cardsSettled] =
    await Promise.allSettled([
      hasReqId
        ? GetPolicyByIdAgent(
            {
              requestCorrelationId: crypto.randomUUID(),
              reqId: reqIdNum,
            },
            SILENT
          )
        : Promise.resolve(null),
      ProductById(productByIdRequest(productId), SILENT),
      GetProductPrograms(detailsReq, SILENT),
      GetProductGroups(detailsReq, SILENT),
      GetProductExcepts(detailsReq, SILENT),
      hasReqId
        ? GetAgentRequestCardsAgent(
            {
              requestCorrelationId: crypto.randomUUID(),
              reqId: reqIdNum,
            },
            SILENT
          )
        : Promise.resolve(null),
    ]);

  const agentRawRow =
    agentSettled.status === "fulfilled" && agentSettled.value
      ? pickData(agentSettled.value)[0]
      : null;

  const agentBase = mapPolicyByIdRow(agentRawRow);

  let product = pickData(settledValue(productSettled))[0] ?? null;
  if (!product) {
    product = await findProductInCatalog(productId);
  }
  if (!product && !agentBase) {
    throw new Error("Product not found");
  }
  if (!product) {
    product = { productId: Number(productId) || productId };
  }

  const progRes = settledValue(progSettled);
  const groupRes = settledValue(groupSettled);

  let exceptions = [];
  if (exceptSettled.status === "fulfilled" && exceptSettled.value) {
    exceptions = mapProductExceptions(pickProductExcepts(exceptSettled.value));
  } else if (POLICY_DETAIL_DEBUG) {
    console.warn("[PolicyDetail] GetProductExcepts failed", {
      productId,
      reason: exceptSettled.reason?.message ?? exceptSettled.status,
    });
  }
  if (!exceptions.length) {
    exceptions = mapExceptNamesFallback(product);
  }

  const prodName = (product.prodName != null ? String(product.prodName).trim() : "") || "";
  const displayName =
    agentBase?.medicalPolicy?.policyFullNumber ||
    prodName ||
    (listRow?.productName && listRow.productName !== "—" ? listRow.productName : "");

  const medicalPolicy = withInvoice(
    agentBase?.medicalPolicy ??
      productMedicalPolicyFallback(product, productId, listRow, displayName),
    listRow
  );

  let insuredCards = [];
  if (cardsSettled.status === "fulfilled" && cardsSettled.value) {
    insuredCards = mapPolicyCards(pickData(cardsSettled.value));
  }

  const productDetailPartial =
    (hasReqId &&
      (agentSettled.status !== "fulfilled" || !agentSettled.value)) ||
    !productSettled ||
    productSettled.status !== "fulfilled" ||
    !progSettled ||
    progSettled.status !== "fulfilled" ||
    exceptSettled.status !== "fulfilled";

  if (productDetailPartial) {
    notifyPolicyDetailPartial(
      agentSettled,
      productSettled,
      progSettled,
      groupSettled,
      exceptSettled,
      cardsSettled
    );
  }

  return {
    policyId: String(product.productId ?? productId),
    reqId: tableReqId,
    detailMode: "product",
    policyFromAgent: Boolean(agentBase),
    productRow: product,
    productName: displayName,
    medicalPolicy,
    adminCharge: agentBase?.adminCharge ?? product.adminCharge ?? null,
    limitPremium: agentBase?.limitPremium ?? null,
    sellerLogin: agentBase?.sellerLogin ?? null,
    agreement: product.paymentCondition ?? product.eligibility ?? null,
    insuredCards,
    exceptions,
    programs: progRes ? mapProductPrograms(pickData(progRes)) : [],
    groups: groupRes ? mapProductGroups(pickData(groupRes)) : [],
    productDetailPartial,
    publicLink:
      pickPublicLink(agentRawRow) ||
      pickPublicLink(product) ||
      pickPublicLink(listRow) ||
      "",
    statusDisplay: listRow?.statusName || listRow?.statusDisplay || "",
    statusId: listRow?.statusId ?? listRow?.StatusId ?? null,
    status: listRow?.status || null,
  };
}



/**

 * @param {string|number} routeId — from router / list row.policyId

 * @param {{ listRow?: object }} [options]

 */

export async function loadPolicyDetail(routeId, options = {}) {

  if (USE_MOCK) {
    if (POLICY_DETAIL_DEBUG) {
      console.warn("[PolicyDetail] VUE_APP_USE_POLICY_MOCK — API Policy/* не вызывается");
    }
    const mock = getPolicyDetailMock(routeId);
    return { ...mock, detailMode: "policy" };
  }

  const listRow = buildDetailListRow(routeId, options.listRow, options.routeQuery);

  const kind =
    listRow?.isPolicyKind ||
    normalizeIsPolicyKind(listRow?.isPolicyRaw ?? listRow?.isPolicy);

  if (POLICY_DETAIL_DEBUG) {
    console.info("[PolicyDetail] route", {
      routeId,
      queryType: options.routeQuery?.type,
      kind: kind || "(agent request)",
      listRow: listRow
        ? { isPolicyKind: listRow.isPolicyKind, medicalPolicyId: listRow.medicalPolicyId }
        : null,
    });
  }

  if (kind === IS_POLICY_POL) {
    const id = listRow?.medicalPolicyId || routeId;
    return loadMedicalPolicyDetail(id, listRow);
  }



  if (kind === IS_POLICY_PROD) {
    const id = listRow?.productId || routeId;
    return loadProductDetail(id, listRow);
  }

  const reqIdFromQuery =
    options.routeQuery?.reqId != null && String(options.routeQuery.reqId).trim() !== ""
      ? String(options.routeQuery.reqId).trim()
      : "";
  if (!kind && reqIdFromQuery && routeId) {
    const catalogProduct = await findProductInCatalog(routeId);
    if (catalogProduct) {
      return loadProductDetail(routeId, {
        ...(listRow && typeof listRow === "object" ? listRow : {}),
        isPolicyKind: IS_POLICY_PROD,
        productId: String(routeId),
        reqId: reqIdFromQuery,
      });
    }
  }

  const reqId = listRow?.reqId || routeId;

  try {
    return await loadAgentRequestDetail(reqId, listRow);
  } catch (agentErr) {
    const catalogProduct = await findProductInCatalog(routeId);
    if (catalogProduct) {
      return loadProductDetail(routeId, listRow);
    }
    throw agentErr;
  }
}


